import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { Session, User, AuthError, SignInWithPasswordCredentials } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient'; // Adjust path if necessary

// Define the shape of the context value
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithPassword: (credentials: SignInWithPasswordCredentials) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
}

// Create the context with a default value (or null/undefined and handle it)
// Using 'null!' assertion as we ensure it's provided before use via the provider check
const AuthContext = createContext<AuthContextType>(null!);

// Define the props for the provider
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(error => {
        console.error("Error getting initial session:", error);
        setLoading(false);
    });

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event, session);
      setSession(session);
      setUser(session?.user ?? null);
      // Typically loading is only true on initial load, not subsequent changes
      // but ensure it's false if an async event happens while initial load is finishing
      if (loading) setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Sign in function
  const signInWithPassword = async (credentials: SignInWithPasswordCredentials) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword(credentials);
    // State update will happen via onAuthStateChange listener
    setLoading(false);
    return { error };
  };

  // Sign out function
  const signOut = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signOut();
    // State update will happen via onAuthStateChange listener
    setLoading(false);
    return { error };
  };

  // Value object for the provider
  const value = {
    user,
    session,
    loading,
    signInWithPassword,
    signOut,
  };

  // Provide the context value to children
  // Only render children when initial loading is complete to avoid flashes of incorrect state
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 